<template>
  <HpsfirProDrawer
    v-model:open="qrcodeDialogVisible"
    :title="`${store?.name || ''} （一店一码）`"
    width="90vw"
    max-width="1280px"
    content-class="store-qrcode-drawer"
    body-class="store-qrcode-drawer__body"
  >
    <div class="qrcode">
      <div class="qrcode-toolbar">
        <el-button type="primary" @click="() => addWeappFormRef?.showDialog()">添加小程序二维码</el-button>
        <el-button type="primary" @click="() => addH5FormRef?.showDialog()">添加H5二维码</el-button>
        <el-button type="primary" @click="() => handleAddDirectQrcode()">添加直开二维码</el-button>
      </div>
      <HpsfirProTable
        v-if="qrcodeDialogVisible"
        ref="tableRef"
        export-file-name="门店二维码"
        row-key="url"
        :columns="columns"
        :get-data="getData"
        :loading="loading"
        :operates="operates"
        :operation-width="380"
        :paginations="{ pageSize: 10 }"
        :hide-add="true"
        :hide-delete="true"
        :hide-edit="true"
      />
    </div>
  </HpsfirProDrawer>
  <HpsfirProForm :is-dialog="true" ref="addWeappFormRef" title="添加小程序二维码" :items="addWeappItems" @ok="handleaddWeappOk"/>
  <HpsfirProForm :is-dialog="true" ref="addH5FormRef" title="添加H5二维码" :items="addH5Items" @ok="handleaddH5Ok"/>
  <HpsfirProDangerDialog
    ref="deleteQrcodeDialogRef"
    title="删除二维码"
    opt-name="删除"
    :on-ok="handleDeleteQrcodeAction"
    :format-dangder-delete-info="formatDeleteQrcodeInfo"
  />
</template>
<script lang="tsx" setup>
import { useApi } from '@/hooks/useApi';
import {
  useBaseAsync,
  type HpsfirProFormItem,
  type HpsfirProTableColumn,
  type HpsfirProTableData,
  type HpsfirProTableFilter,
  type HpsfirProTableGetDataFunction,
} from 'hpsfir-managebase-ui';

import { keyBy } from 'lodash';
import { ref } from 'vue';
import QrcodeVue from 'qrcode.vue';
import { ElMessage } from 'element-plus';
import { convertBase64UrlToImgFile } from '@/utils';
import { normalizeBizTableColumns } from '@/utils/tableColumns';

interface QrcodeRow {
  name: string;
  appid?: string;
  h5TplId?: string;
  url: string;
}

const addWeappFormRef = ref<any>();
const addWeappItems = ref<HpsfirProFormItem[]>([]);
const addH5FormRef = ref<any>();
const addH5Items = ref<HpsfirProFormItem[]>([]);
const tableRef = ref<any>();
const deleteQrcodeDialogRef = ref<any>();
const emit = defineEmits<{(e: 'setDownload'): void;}>();
const store = ref<any>({});

const qrcodeDialogVisible = ref(false);

// 获取的二维码值
const qrcodeValues = ref<QrcodeRow[]>([]);

const loading = ref(false);

const columns = ref<HpsfirProTableColumn[]>(normalizeBizTableColumns([
  {
    name: 'name',
    label: '名称',
    type: 'string',
    width: 180,
  },
  {
    name: 'url',
    label: '二维码',
    type: 'string',
    width: 180,
    render: (scope: any) => {
      const row = scope?.row as QrcodeRow | undefined;
      if (!row?.url) {
        return '';
      }
      return (
        <QrcodeVue
          id={`qrcode-${scope.$index}`}
          value={row.url}
          size={800}
          style="width: 100px; height: 100px"
        />
      );
    },
  },
  {
    name: 'downloadQrcodeUrl',
    label: '客户端下载',
    type: 'string',
    render: (scope: any) => (store.value.downloadQrcodeUrl === scope?.row?.url ? '是' : ''),
  },
]));

const saveQrcode = (qrcode: QrcodeRow, inx: any) => {
  const qrcodeEl: any = document.getElementById(`qrcode-${inx}`);
  if (!qrcodeEl) {
    return;
  }
  const dataURL = qrcodeEl.toDataURL('image/png');

  const link = document.createElement('a');
  link.href = dataURL;
  link.download = `qrcode-${qrcode.name}.png`;
  link.style.display = 'none';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const getData = useBaseAsync<HpsfirProTableGetDataFunction>((filter: HpsfirProTableFilter) => {
  const page = filter.paginations.page || 1;
  const pageSize = filter.paginations.pageSize || 10;
  const start = (page - 1) * pageSize;
  const data = qrcodeValues.value.slice(start, start + pageSize);
  return {
    total: qrcodeValues.value.length,
    data,
  } as HpsfirProTableData;
});

const init = useBaseAsync(async() => {
  try {
    loading.value = true;
    qrcodeValues.value = [];
    // 获取小程序列表
    const weappRes = await useApi().wxApi.getRelativeWeappwx({ appid: store.value.wxAppid });
    const weappOptions = weappRes?.data?.data?.filter((item: any) => item.status === 1).map((item: any) => ({
      label: item.nickname,
      value: item.appid,
    }));
    // 获取h5模板列表
    const h5TplRes = await useApi().wxTplApi.listWxTplByTenantwxTpl({ });
    const h5TplOptions = h5TplRes?.data?.data?.map((item: any) => ({
      label: `${item.name}(${item.url})`,
      value: item.id,
    }));

    addWeappItems.value = [
      {
        name: 'appid',
        label: '小程序',
        type: 'select',
        rule: [{ required: true }],
        options: weappOptions,
      },
    ];

    addH5Items.value = [
      {
        name: 'id',
        label: 'H5模板',
        type: 'select',
        rule: [{ required: true }],
        options: h5TplOptions,
      },
    ];

    const weappMap = keyBy(weappOptions as any, (item) => String(item.value));
    const res = await useApi().storeApi.getQrcodesstore({
      storeId: store.value.id,
    });
    Object.entries(res?.data?.data?.weappQrcode || {}).forEach(([appid, url]: any) => {
      const weapp = weappMap[appid];
      if (!weapp) {
        return;
      }
      qrcodeValues.value.push({
        name: `${weapp.label}(小程序)`,
        appid,
        url,
      });
    });

    const h5Map = keyBy(h5TplOptions as any, (item) => item.value);
    Object.entries(res?.data?.data?.wxQrcode || {}).forEach(([h5TplId, url]: any) => {
      const h5Tpl = h5Map[String(h5TplId)];
      if (!h5Tpl) {
        return;
      }
      qrcodeValues.value.push({
        name: `${h5Tpl.label}`,
        h5TplId: String(h5TplId),
        url,
      });
    });

    if (res?.data?.data?.directQrcodeUrl) {
      qrcodeValues.value.push({
        name: '直开',
        url: res?.data?.data?.directQrcodeUrl,
      });
    }
    store.value.downloadQrcodeUrl = res.data.data.downloadQrcodeUrl ? res.data.data.downloadQrcodeUrl.split('_#_#_')[0] : '';
    tableRef.value?.refresh?.();
  } finally {
    loading.value = false;
  }
});

const setDownloadQrcode = useBaseAsync(async(qrcode: QrcodeRow, inx: any) => {
  const qrcodeEl: any = document.getElementById(`qrcode-${inx}`);
  if (!qrcodeEl) {
    return;
  }
  const dataURL = qrcodeEl.toDataURL('image/png');
  try {
    const res = await useApi().uploadApi.imageUpload([convertBase64UrlToImgFile(dataURL, 'qrcode.png').file]);

    if (!res.data.data?.[0]) {
      ElMessage.error('设置失败！');
      return;
    }

    await useApi().storeApi.setDownloadQrcodestore({
      storeId: store.value.id,
      qrcodeUrl: `${qrcode.url}_#_#_${res.data.data?.[0]}`,
    });
    init();
    emit('setDownload');
  } catch (error) {
    console.log(error);
  }
});

const handleaddWeappOk = useBaseAsync(async(data: any) => {
  await useApi().storeApi.addWeappQrcodestore({
    storeId: store.value.id,
    appid: data.appid,
  });
  ElMessage.success('添加成功');
  init();
});

const handleaddH5Ok = useBaseAsync(async(data: any) => {
  await useApi().storeApi.addH5Qrcodestore({
    storeId: store.value.id,
    h5TplId: data.id,
  });
  ElMessage.success('添加成功');
  init();
});

const handleAddDirectQrcode = useBaseAsync(async() => {
  await useApi().storeApi.addDirectQrcodeUrlstore({
    storeId: store.value.id,
  });
  ElMessage.success('添加成功');
  init();
});

const formatDeleteQrcodeInfo = (row: any) => `您正在进行删除二维码(${row?.name})操作，请谨慎操作`;

const handleDeleteQrcodeAction = useBaseAsync(async(data: QrcodeRow) => {
  if (data.name === '直开') {
    await useApi().storeApi.delDirectQrcodestore({
      storeId: store.value.id,
    });
  } else if (data.name.includes('(小程序)')) {
    await useApi().storeApi.delWeappQrcodestore({
      storeId: store.value.id,
      appid: data.appid,
    });
  } else {
    await useApi().storeApi.delWxQrcodestore({
      storeId: store.value.id,
      h5TplId: data.h5TplId,
    });
  }

  ElMessage.success('删除成功');
  init();
});

const delQrcode = (data: QrcodeRow) => {
  deleteQrcodeDialogRef.value?.showDialog(data as unknown as Record<string, unknown>);
};

const operates: any[] = [
  {
    key: 'saveQrcode',
    name: '下载二维码',
    handler: (row: QrcodeRow, index: number) => saveQrcode(row, index),
  },
  {
    key: 'setDownloadQrcode',
    name: '设置客户端下载',
    handler: (row: QrcodeRow, index: number) => setDownloadQrcode(row, index),
  },
  {
    key: 'delQrcode',
    name: '删除',
    handler: (row: QrcodeRow) => delQrcode(row),
  },
];

defineExpose({
  show: async(_store: any) => {
    store.value = { ..._store };
    qrcodeDialogVisible.value = true;
    await init();
  },
});

</script>
<style lang="scss">
.store-qrcode-drawer {
  width: min(1280px, 90vw) !important;

  @media (max-width: 767px) {
    width: 100vw !important;
    max-width: 100vw !important;
  }
}

.store-qrcode-drawer__body {
  padding: 12px !important;

  @media (max-width: 767px) {
    padding: 8px !important;
  }
}

.qrcode-toolbar {
  margin-bottom: 12px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
</style>
